import type { Keyword } from './types';

// Common English stop words to filter out
const STOP_WORDS = new Set([
  'a',
  'an',
  'the',
  'and',
  'or',
  'but',
  'in',
  'on',
  'at',
  'to',
  'for',
  'of',
  'with',
  'by',
  'from',
  'as',
  'is',
  'was',
  'are',
  'were',
  'been',
  'be',
  'have',
  'has',
  'had',
  'do',
  'does',
  'did',
  'will',
  'would',
  'could',
  'should',
  'may',
  'might',
  'must',
  'shall',
  'can',
  'need',
  'dare',
  'ought',
  'used',
  'i',
  'me',
  'my',
  'myself',
  'we',
  'our',
  'ours',
  'ourselves',
  'you',
  'your',
  'yours',
  'yourself',
  'yourselves',
  'he',
  'him',
  'his',
  'himself',
  'she',
  'her',
  'hers',
  'herself',
  'it',
  'its',
  'itself',
  'they',
  'them',
  'their',
  'theirs',
  'themselves',
  'what',
  'which',
  'who',
  'whom',
  'this',
  'that',
  'these',
  'those',
  'am',
  'being',
  'if',
  'because',
  'until',
  'while',
  'about',
  'into',
  'through',
  'during',
  'before',
  'after',
  'above',
  'below',
  'between',
  'under',
  'again',
  'further',
  'then',
  'once',
  'here',
  'there',
  'when',
  'where',
  'why',
  'how',
  'all',
  'each',
  'few',
  'more',
  'most',
  'other',
  'some',
  'such',
  'no',
  'nor',
  'not',
  'only',
  'own',
  'same',
  'so',
  'than',
  'too',
  'very',
  's',
  't',
  'just',
  'don',
  'now',
  'create',
  'make',
  'template',
  'note',
  'please',
  'want',
  'like',
  'get',
]);

// Container dimensions (must match templateAssemblyLoader.tsx)
const CONTAINER_WIDTH = 320;
const CONTAINER_HEIGHT = 200;
const PADDING = 16; // Padding from edges
const MIN_SPACING = 4; // Minimum 4px spacing between badges

// Badge sizing constants (matching Badge component xs size: px-1 py-0.5 text-[0.625rem])
const BADGE_PADDING_X = 8; // px-1 = 4px each side
const CHAR_WIDTH = 5; // Approximate character width at 10px (0.625rem) font
const BADGE_HEIGHT = 14; // Height of badge with py-0.5 (2px each side) + 10px text

/**
 * Estimate badge dimensions based on text content
 */
function estimateBadgeDimensions(text: string): { width: number; height: number } {
  const textWidth = text.length * CHAR_WIDTH;
  return {
    width: textWidth + BADGE_PADDING_X,
    height: BADGE_HEIGHT,
  };
}

/**
 * Check if two rectangles overlap (with minimum spacing)
 */
function checkOverlap(
  a: { x: number; y: number; width: number; height: number },
  b: { x: number; y: number; width: number; height: number },
  spacing: number = MIN_SPACING
): boolean {
  return !(
    a.x + a.width + spacing <= b.x ||
    b.x + b.width + spacing <= a.x ||
    a.y + a.height + spacing <= b.y ||
    b.y + b.height + spacing <= a.y
  );
}

/**
 * Check if a keyword overlaps with any existing keywords
 */
function hasCollision(
  keyword: { x: number; y: number; width: number; height: number },
  existingKeywords: Keyword[]
): boolean {
  return existingKeywords.some((existing) => checkOverlap(keyword, existing));
}

/**
 * Zone definitions for distributing keywords evenly across the container.
 * Each zone is defined as a region (x%, y%, width%, height%) of the usable area.
 * Zones are designed to cover corners, edges, and center for visual balance.
 */
interface Zone {
  xPercent: number; // X position as percentage of usable width
  yPercent: number; // Y position as percentage of usable height
  // Add some jitter range for natural feel
  jitterX: number;
  jitterY: number;
}

/**
 * Get zone layouts optimized for different keyword counts.
 * These layouts ensure even distribution across the 2D space.
 */
function getZoneLayout(count: number): Zone[] {
  // Layouts designed for visual balance across the rectangle
  const layouts: Record<number, Zone[]> = {
    // 1 keyword: center
    1: [{ xPercent: 0.5, yPercent: 0.5, jitterX: 0.15, jitterY: 0.15 }],

    // 2 keywords: diagonal spread
    2: [
      { xPercent: 0.25, yPercent: 0.35, jitterX: 0.12, jitterY: 0.12 },
      { xPercent: 0.75, yPercent: 0.65, jitterX: 0.12, jitterY: 0.12 },
    ],

    // 3 keywords: triangle formation
    3: [
      { xPercent: 0.5, yPercent: 0.2, jitterX: 0.15, jitterY: 0.08 }, // Top center
      { xPercent: 0.2, yPercent: 0.7, jitterX: 0.1, jitterY: 0.1 }, // Bottom left
      { xPercent: 0.8, yPercent: 0.65, jitterX: 0.1, jitterY: 0.1 }, // Bottom right
    ],

    // 4 keywords: four quadrants
    4: [
      { xPercent: 0.25, yPercent: 0.25, jitterX: 0.1, jitterY: 0.1 }, // Top left
      { xPercent: 0.75, yPercent: 0.25, jitterX: 0.1, jitterY: 0.1 }, // Top right
      { xPercent: 0.25, yPercent: 0.75, jitterX: 0.1, jitterY: 0.1 }, // Bottom left
      { xPercent: 0.75, yPercent: 0.75, jitterX: 0.1, jitterY: 0.1 }, // Bottom right
    ],

    // 5 keywords: four corners + center (X pattern)
    5: [
      { xPercent: 0.2, yPercent: 0.2, jitterX: 0.08, jitterY: 0.08 }, // Top left
      { xPercent: 0.8, yPercent: 0.2, jitterX: 0.08, jitterY: 0.08 }, // Top right
      { xPercent: 0.5, yPercent: 0.5, jitterX: 0.1, jitterY: 0.1 }, // Center
      { xPercent: 0.2, yPercent: 0.8, jitterX: 0.08, jitterY: 0.08 }, // Bottom left
      { xPercent: 0.8, yPercent: 0.8, jitterX: 0.08, jitterY: 0.08 }, // Bottom right
    ],

    // 6 keywords: 2 rows of 3
    6: [
      { xPercent: 0.17, yPercent: 0.3, jitterX: 0.08, jitterY: 0.1 },
      { xPercent: 0.5, yPercent: 0.25, jitterX: 0.1, jitterY: 0.1 },
      { xPercent: 0.83, yPercent: 0.3, jitterX: 0.08, jitterY: 0.1 },
      { xPercent: 0.17, yPercent: 0.7, jitterX: 0.08, jitterY: 0.1 },
      { xPercent: 0.5, yPercent: 0.75, jitterX: 0.1, jitterY: 0.1 },
      { xPercent: 0.83, yPercent: 0.7, jitterX: 0.08, jitterY: 0.1 },
    ],
  };

  // Default to 5-keyword layout for larger counts or unknown counts
  return layouts[count] ?? layouts[5] ?? [];
}

/**
 * Calculate position within a zone, accounting for badge dimensions
 */
function getPositionInZone(
  zone: Zone,
  badgeWidth: number,
  badgeHeight: number,
  existingKeywords: Keyword[],
  maxAttempts: number = 20
): { x: number; y: number } | null {
  const usableWidth = CONTAINER_WIDTH - PADDING * 2;
  const usableHeight = CONTAINER_HEIGHT - PADDING * 2;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    // Calculate base position from zone percentage
    const baseX = PADDING + zone.xPercent * usableWidth;
    const baseY = PADDING + zone.yPercent * usableHeight;

    // Add jitter for natural feel
    const jitterX = (Math.random() - 0.5) * 2 * zone.jitterX * usableWidth;
    const jitterY = (Math.random() - 0.5) * 2 * zone.jitterY * usableHeight;

    // Center the badge on the position
    let x = baseX + jitterX - badgeWidth / 2;
    let y = baseY + jitterY - badgeHeight / 2;

    // Clamp to container bounds
    x = Math.max(PADDING, Math.min(x, CONTAINER_WIDTH - PADDING - badgeWidth));
    y = Math.max(PADDING, Math.min(y, CONTAINER_HEIGHT - PADDING - badgeHeight));

    const candidate = { x, y, width: badgeWidth, height: badgeHeight };
    if (!hasCollision(candidate, existingKeywords)) {
      return { x, y };
    }
  }

  return null;
}

/**
 * Extract meaningful keywords from user input text
 * @param input - The user's input text
 * @param maxKeywords - Maximum number of keywords to extract (default: 5)
 * @returns Array of Keyword objects with evenly distributed positions
 */
export function extractKeywords(input: string, maxKeywords = 5): Keyword[] {
  if (!input || typeof input !== 'string') {
    return [];
  }

  // Tokenize: split by whitespace and punctuation, lowercase
  const words = input
    .toLowerCase()
    .replace(/[^\w\s-]/g, ' ')
    .split(/\s+/)
    .filter((word) => word.length > 2) // Filter out very short words
    .filter((word) => !STOP_WORDS.has(word)) // Remove stop words
    .filter((word) => !/^\d+$/.test(word)); // Remove pure numbers

  // Count word frequency
  const wordCounts = new Map<string, number>();
  for (const word of words) {
    wordCounts.set(word, (wordCounts.get(word) || 0) + 1);
  }

  // Sort by frequency (descending), then by word length (prefer longer words)
  const sortedWords = [...wordCounts.entries()]
    .sort((a, b) => {
      if (b[1] !== a[1]) return b[1] - a[1]; // Higher frequency first
      return b[0].length - a[0].length; // Longer words first for ties
    })
    .slice(0, maxKeywords)
    .map(([word]) => word);

  // If we don't have enough keywords, try to get unique words from the original
  let wordsToUse = sortedWords;
  if (sortedWords.length < 3 && words.length > 0) {
    wordsToUse = [...new Set(words)].slice(0, maxKeywords);
  }

  // Get zone layout for this keyword count
  const zones = getZoneLayout(wordsToUse.length);

  // Create keywords with zone-based positioning
  const keywords: Keyword[] = [];
  for (const [i, text] of wordsToUse.entries()) {
    const dimensions = estimateBadgeDimensions(text);
    const zone = zones[i % zones.length];

    if (!zone) continue;

    const position = getPositionInZone(
      zone,
      dimensions.width,
      dimensions.height,
      keywords
    );

    if (position) {
      keywords.push({
        id: `keyword-${i}-${text}`,
        text,
        x: position.x,
        y: position.y,
        width: dimensions.width,
        height: dimensions.height,
      });
    }
  }

  return keywords;
}

/**
 * Get a short context string from the user input for status messages
 * @param input - The user's input text
 * @param maxLength - Maximum length of the context string
 */
export function getContextSummary(input: string, maxLength = 30): string {
  if (!input || typeof input !== 'string') {
    return '';
  }

  const trimmed = input.trim();
  if (trimmed.length <= maxLength) {
    return trimmed;
  }

  // Find a good break point (space, comma, etc.)
  const truncated = trimmed.slice(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');

  if (lastSpace > maxLength * 0.5) {
    return truncated.slice(0, lastSpace) + '…';
  }

  return truncated + '…';
}
